import { observer } from 'mobx-react-lite';
import React from 'react';

import { AccordionV2 } from '@/components/common/AccordionV2/AccordionV2';
import InputElement from '@/components/common/InputElement/InputElement';
import FilterSection from '@/components/menuV2/FilterSection/FilterSection';
import type {
  FilterRecordModelType,
  MenuSortOptionRecordModelType,
} from '@/models/api';
import { useStore } from '@/models/root-store';
import { LoadingState } from '@/utils/constants';

interface FilterProps {
  search?: boolean;
  sorts: MenuSortOptionRecordModelType[];
  filtersData?: FilterRecordModelType['sections'];
  isSortSelected: (sort: string | undefined | null) => boolean;
  setCurrentSelectedFilter: (key: string) => void;
  filters: any;
  setFilter: (key: string, value: string) => void;
  isFiltered: (key: string, value: string) => boolean;
}

const DesktopFilter = observer(
  ({
    search = false,
    sorts,
    filtersData,
    isSortSelected,
    setCurrentSelectedFilter,
    filters,
    setFilter,
    isFiltered,
  }: FilterProps) => {
    const store = useStore();
    const {
      productStore: {
        productsCount,
        isFiltering,
        productState,
        filters: filterStore,
      },
    } = store;

    const renderSortAccordion = () => (
      <AccordionV2 title="Sort" className="mb-2 w-full" expanded={!search}>
        <div className="flex flex-col space-y-2 border-b pb-5">
          {sorts?.map((sort) => (
            <InputElement
              key={sort.id}
              label={sort.name as string}
              type="radio"
              checked={isSortSelected(sort.value)}
              onChange={() => {
                setCurrentSelectedFilter('sort');
                filters.setSort(sort.value);
              }}
            />
          ))}
        </div>
      </AccordionV2>
    );

    const renderFilterAccordion = (
      // @ts-ignore
      filter: FilterRecordModelType['sections'][number],
      index: number,
    ) => {
      if (
        !productsCount &&
        filterStore.isFiltered &&
        !isFiltering &&
        productState === LoadingState.DONE &&
        search &&
        index === 0
      ) {
        return null;
      }

      return (
        <AccordionV2
          key={filter.id}
          title={filter.name || ''}
          className="mb-2 w-full"
          expanded={search && index === 0}
          hideArrow={search && index === 0}
        >
          <FilterSection
            search={search}
            filter={filter}
            setFilter={setFilter}
            isFiltered={isFiltered}
            setCurrentSelectedFilter={setCurrentSelectedFilter}
          />
        </AccordionV2>
      );
    };

    const renderContent = () => {
      const filtersArray = filtersData ? filtersData.slice() : [];

      if (search && filtersArray.length > 0) {
        // In search mode, render the first filter, then sort, then other filters
        const [firstFilter, ...restFilters] = filtersArray;
        return (
          <>
            {renderFilterAccordion(firstFilter, 0)}
            {renderSortAccordion()}
            {restFilters.map((filter, index) =>
              renderFilterAccordion(filter, index + 1),
            )}
          </>
        );
      }
      // Not in search mode, render sort before filters
      return (
        <>
          {renderSortAccordion()}
          {filtersArray.map((filter, index) =>
            renderFilterAccordion(filter, index),
          )}
        </>
      );
    };

    return <div className="flex flex-col">{renderContent()}</div>;
  },
);

export default DesktopFilter;
